/**
 * client/src/main.tsx — the entry point `client/index.html` loads.
 *
 * Four lines of work, and the order of the first two is the only interesting thing in the file.
 *
 * ⚠️ **`startTheme()` runs BEFORE `createRoot`, and not from an effect.** `ui/tokens.css` is
 * light-first: `:root` is day and `.dark` is night (it is lifted byte-identical from audiobook and
 * must stay diffable, so the night palette is a class rather than a media query — see that file's
 * header). A class written in `useEffect` lands *after* the first paint, so a laptop set to dark mode
 * gets one frame of a cream page at full brightness. On this product that frame is the first thing
 * anybody ever sees of it, in a living room, in the evening.
 *
 * ⚠️ **`<SessionProvider>` is above everything**, so no screen can render before
 * `GET /api/v1/session` has been *asked*. It is VOX-LAW 20's only cookie-setting route and it is what
 * mints the `gst_` identity that owns an anonymous session — which on this site is the primary flow,
 * not a degraded one (§2.3).
 *
 * ── On `StrictMode` ─────────────────────────────────────────────────────────────────────────────
 *
 * It is on, and it double-invokes effects in development. Two effects in this tree would be actively
 * harmful if that were not handled, and both are handled with a `useRef` guard rather than by turning
 * StrictMode off: the boot request in `lib/session.tsx` (two requests from a browser with no cookie
 * is **two guest identities**, and §11.3 meters quota per `actorKey`), and `POST /complete` in
 * `pages/Wait.tsx` (two jobs on the one serialized GPU for one woman's session). Turning StrictMode
 * off would hide both classes of bug rather than fix them, on a tree several agents are writing at
 * once.
 *
 * ── What is deliberately NOT here ───────────────────────────────────────────────────────────────
 *
 * audiobook's `main.tsx` calls `startOfflineRuntime()` — a service worker and an outbox. **There is
 * no service worker in this repo and there must not be one**, and `vite.config.ts` says why at
 * length: a studio session is a single uninterrupted page visit with an open microphone, and a cached
 * shell that serves an older `capture.worklet.ts` than the `encode.worker.ts` beside it is a format
 * mismatch between two threads with nothing in any log.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { SessionProvider } from "./lib/session.tsx";
import { startTheme } from "./lib/theme.ts";
import "./ui/app.css";
import "./ui/polish.css";

startTheme();

const root = document.getElementById("root");
if (!root) {
  // `index.html` carries `<div id="root">`. If it is missing, the shell was edited or something in
  // front of the app served a different document — Caddy, a captive portal, or `@fastify/static`
  // indexing a stale `client/dist` (§12.3: `npm run build` BEFORE `systemctl restart`, always).
  // Saying which beats a blank page with nothing in any log.
  throw new Error("#root is missing from index.html — the app shell was not the one this bundle expects.");
}

createRoot(root).render(
  <StrictMode>
    <SessionProvider>
      <App />
    </SessionProvider>
  </StrictMode>,
);
